import { Controller } from "@hotwired/stimulus";
import {
  initAddTagsModal,
  showAddTagsModal,
  applyContentTags,
  executeAddToLibrary,
  removeFromLibrary,
  setAddButtonState,
  setRemoveButtonState,
} from "../shared/library_tags";
import {
  observeSentinel,
  disconnectObserver,
  scrollToTop,
} from "../shared/infinite_scroll";

export default class extends Controller {
  static targets = ["query", "results", "form", "grid", "sentinel", "spinner"];
  static values = { url: String, hasFriends: Boolean };

  connect() {
    this.debounceTimer = null;
    this.loading = false;
    observeSentinel(this);
    this.restoreFilters();
    this.addTagsModal = initAddTagsModal();
  }

  disconnect() {
    clearTimeout(this.debounceTimer);
    disconnectObserver(this);
  }

  debounceSearch() {
    clearTimeout(this.debounceTimer);
    const value = this.queryTarget.value.trim();

    if (value.length === 0 || value.length >= 2) {
      this.debounceTimer = setTimeout(() => {
        this.saveFilters();
        this.fetchResults();
      }, 500);
    }
  }

  filter() {
    this.saveFilters();
    this.fetchResults();
  }

  saveFilters() {
    const formData = new FormData(this.formTarget);
    const data = {};
    for (const [key, value] of formData.entries()) {
      data[key] = value;
    }
    localStorage.setItem("bingeflow_search_filters", JSON.stringify(data));
  }

  restoreFilters() {
    const saved = localStorage.getItem("bingeflow_search_filters");
    if (!saved) return;
    const data = JSON.parse(saved);
    const form = this.formTarget;
    const query = form.querySelector('[name="q"]');
    if (query && data.q) query.value = data.q;
    const contentType = form.querySelector('[name="content_type"]');
    if (contentType && data.content_type) contentType.value = data.content_type;
    const provider = form.querySelector('[name="provider"]');
    if (provider && data.provider) provider.value = data.provider;
    const category = form.querySelector('[name="category"]');
    if (category && data.category) category.value = data.category;
    const libraryFilter = form.querySelector('[name="library_filter"]');
    if (libraryFilter && data.library_filter)
      libraryFilter.value = data.library_filter;
    this.fetchResults();
  }

  fetchResults() {
    const formData = new FormData(this.formTarget);
    const params = new URLSearchParams(formData).toString();
    const url = this.urlValue + "?" + params;

    fetch(url, {
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
      .then((response) => response.text())
      .then((html) => {
        this.resultsTarget.innerHTML = html;
        observeSentinel(this);
      });
  }

  loadMore() {
    if (this.loading || !this.hasSentinelTarget) return;

    const nextPage = this.sentinelTarget.dataset.nextPage;
    if (!nextPage) return;

    this.loading = true;
    disconnectObserver(this);
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("d-none");

    const formData = new FormData(this.formTarget);
    formData.set("page", nextPage);
    const params = new URLSearchParams(formData).toString();
    const url = this.urlValue + "?" + params;

    fetch(url, {
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
      .then((response) => response.text())
      .then((html) => {
        if (this.hasSpinnerTarget) this.spinnerTarget.remove();
        if (this.hasSentinelTarget) this.sentinelTarget.remove();

        const temp = document.createElement("div");
        temp.innerHTML = html;

        const cards = temp.querySelectorAll(".search-content-wrapper");
        cards.forEach((card) => this.gridTarget.appendChild(card));

        const match = html.match(/<!-- next_page:(\d+) -->/);
        if (match) {
          const sentinel = document.createElement("div");
          sentinel.dataset.searchTarget = "sentinel";
          sentinel.dataset.nextPage = match[1];
          this.resultsTarget.appendChild(sentinel);

          const spinner = document.createElement("div");
          spinner.className = "search-loading-spinner";
          spinner.dataset.searchTarget = "spinner";
          spinner.innerHTML =
            '<div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div>';
          this.resultsTarget.insertBefore(spinner, sentinel);

          observeSentinel(this);
        }

        this.loading = false;
      });
  }

  addToLibrary(event) {
    event.preventDefault();
    event.stopPropagation();

    const btn = event.currentTarget;
    const slug = btn.dataset.contentSlug;
    const name = btn.dataset.contentName;

    if (this.hasFriendsValue) {
      showAddTagsModal(this.addTagsModal, slug, name, {
        onApply: (checkboxes) => {
          this.doAddToLibrary(btn, slug, true).then(() =>
            applyContentTags(slug, checkboxes).then(() =>
              this.addTagsModal.hide(),
            ),
          );
        },
        onSkip: () => {
          this.doAddToLibrary(btn, slug).then(() =>
            this.addTagsModal.hide(),
          );
        },
      });
      return;
    }

    this.doAddToLibrary(btn, slug);
  }

  doAddToLibrary(btn, slug, skipEvent = false) {
    btn.disabled = true;

    return executeAddToLibrary(slug, { skipEvent })
      .then(() => {
        setAddButtonState(btn, "search");
        this.updateLibraryCounts(1);
      })
      .catch(() => {
        btn.disabled = false;
      });
  }

  removeFromLibrary(event) {
    event.preventDefault();
    event.stopPropagation();

    const btn = event.currentTarget;
    this.pendingRemoveSlug = btn.dataset.contentSlug;
    this.pendingRemoveBtn = btn;

    const name = btn.dataset.contentName;
    document.getElementById("searchRemoveContentName").textContent = name;
    document.getElementById("searchRemoveContentNameLabel").textContent = name;

    const checkbox = document.getElementById("searchRemoveCheckbox");
    const confirmBtn = document.getElementById("searchRemoveConfirmBtn");
    checkbox.checked = false;
    confirmBtn.disabled = true;

    const modal = new bootstrap.Modal(
      document.getElementById("searchRemoveModal"),
    );
    this.removeModal = modal;

    checkbox.onchange = () => {
      confirmBtn.disabled = !checkbox.checked;
    };
    confirmBtn.onclick = () => this.confirmRemove();

    modal.show();
  }

  confirmRemove() {
    const slug = this.pendingRemoveSlug;
    const btn = this.pendingRemoveBtn;
    const confirmBtn = document.getElementById("searchRemoveConfirmBtn");
    confirmBtn.disabled = true;

    removeFromLibrary(slug)
      .then(() => {
        this.removeModal.hide();
        setRemoveButtonState(btn, "search");
        this.updateLibraryCounts(-1);
      })
      .catch(() => {
        confirmBtn.disabled = false;
      });
  }

  scrollToTop(event) {
    scrollToTop(event);
  }

  updateLibraryCounts(delta) {
    const select = this.formTarget.querySelector(
      'select[name="library_filter"]',
    );
    if (!select) return;

    [
      ["my_content", delta],
      ["not_my_content", -delta],
    ].forEach(([value, d]) => {
      const option = select.querySelector(`option[value="${value}"]`);
      if (!option) return;
      const match = option.textContent.match(/^(.*?)\s*\((\d+)\)$/);
      if (match) option.textContent = `${match[1]} (${parseInt(match[2]) + d})`;
    });
  }
}
